import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

// Re-export shared utilities for easy import in frontend components
export {
  getCommissionRate,
  getCommissionRateAsync,
  getProductCategory,
  calculateProductCommission,
  calculateProductCommissionAsync,
  calculatePaymentRatio,
  getPaymentsInPeriod,
  getAllPaidPayments,
  calculatePendingAmount,
} from "@/shared/lib/commission-utils";

// Re-export general date utilities from dataslot-utils
export {
  toBangkokTimestamp,
  toBangkokTimestampEndOfDay,
  toBangkokISOString,
  formatCurrency,
  formatPercentage,
} from "@x-veloper/dataslot-utils";

// Re-export commission-specific date utilities
export {
  getCommissionPeriod,
  getFullMonthRange,
  getCustomDateRange,
  getPreviousCommissionPeriod,
  getNextCommissionPeriod,
  isInCommissionPeriod,
  formatCommissionPeriod,
} from "@/shared/lib/commission-date-utils";

// Frontend-specific utilities
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Debounce function for search input
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Throttle function for performance optimization
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number,
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;

  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * Check if code is running in development mode
 */
export function isDev(): boolean {
  return process.env.NODE_ENV === "development";
}

/**
 * Safe JSON parse with fallback
 */
export function safeJsonParse<T>(json: string, fallback: T): T {
  try {
    return JSON.parse(json) as T;
  } catch {
    return fallback;
  }
}

/**
 * Generate a random ID
 */
export function generateId(length: number = 8): string {
  return Math.random()
    .toString(36)
    .substring(2, length + 2);
}